import React from 'react';
import { ChevronDownIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type SelectOption = {
  value: string | number;
  label: string;
};

/**
 * @deprecated Use QueryCombobox for new query selectors. It supports single and
 * multiple selection, search, and custom option/value rendering.
 */
export type ISelectProps = {
  className?: string;
  onCheck?: (value: SelectOption, checked: boolean) => void;
  options: SelectOption[];
  menuLabel?: string;
  placeholder?: string;
  icon?: React.ReactNode;
  disableOnSingle?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
} & (
  | {
      multiple: true;
      value?: SelectOption[];
      plural: string;
      onChange?: (value: SelectOption[]) => void;
    }
  | {
      multiple?: false;
      value?: SelectOption;
      plural?: string;
      onChange?: (value: SelectOption) => void;
    }
);

/**
 * @deprecated Use QueryCombobox for new query selectors. It supports single and
 * multiple selection, search, and custom option/value rendering.
 */
const Select: React.FC<ISelectProps> = (props) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        size="sm"
        variant="outline"
        className={cn('w-full justify-between', props.className)}
        disabled={props.disabled}
      >
        {props.multiple
          ? props.value?.length === 1
            ? props.value[0].label
            : `${props.value?.length ?? 0} ${props.plural}`
          : props.value?.label || (
              <span className="text-muted-foreground font-normal">
                {props.placeholder}
              </span>
            )}
        {props.icon || <ChevronDownIcon className="ml-2 h-4 w-4" />}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      {props.menuLabel && (
        <DropdownMenuLabel>{props.menuLabel}</DropdownMenuLabel>
      )}
      {props.options.map((option) => {
        const isChecked = props.multiple
          ? !!props.value?.find(
              (valueOption) => valueOption.value === option.value,
            )
          : props.value?.value === option.value;

        const isSingle = !!(props.multiple && props.value?.length === 1);
        const isDisabled =
          props.readOnly ||
          (props.disableOnSingle && isSingle && !!props.value && isChecked);

        return (
          <DropdownMenuCheckboxItem
            key={option.value}
            disabled={isDisabled}
            checked={isChecked}
            onClick={() => {
              props.onCheck?.(option, isChecked);

              if (!props.onChange) return;

              props.onChange(
                (props.multiple
                  ? isChecked
                    ? (props.value?.filter((v) => v.value !== option.value) ??
                      [])
                    : [...(props.value ?? []), option]
                  : option) as SelectOption[] & SelectOption,
              );
            }}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        );
      })}
    </DropdownMenuContent>
  </DropdownMenu>
);

export default Select;
